/**
 * AdminView Component
 * Admin panel for managing component types and system settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  Category as CategoryIcon,
  MoreVert as MoreIcon,
  MoveUp as MoveIcon,
  PlaylistAdd as SeedIcon,
} from '@mui/icons-material';
import { useBOM } from '../contexts/BOMContext';

interface ComponentType {
  id: number;
  name: string;
  display_name: string;
  description: string | null;
  iec_prefix: string | null;
  category: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string | null;
}

interface CategoryStats {
  component_type: string;
  count: number;
  in_master_list: boolean;
  display_name: string;
  category: string;
  is_active: boolean;
}

const AdminView: React.FC = () => {
  const { showToast } = useBOM();
  
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [componentTypes, setComponentTypes] = useState<ComponentType[]>([]);
  const [categoryStats, setCategoryStats] = useState<CategoryStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<ComponentType | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    iec_prefix: '',
    category: '',
    is_active: true,
    sort_order: 0,
  });
  
  // Context menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedStat, setSelectedStat] = useState<CategoryStats | null>(null);
  
  // Move category dialog
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveToCategory, setMoveToCategory] = useState('');

  // Fetch component types
  const fetchComponentTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/component-types');
      const data = await response.json();
      if (data.success) {
        setComponentTypes(data.component_types);
      } else {
        throw new Error(data.detail || 'Failed to load component types');
      }
    } catch (err: any) {
      setError(err.message);
      showToast('error', err.message);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Fetch category stats
  const fetchCategoryStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/category-stats');
      const data = await response.json();
      if (data.success) {
        setCategoryStats(data.stats);
      }
    } catch (err: any) {
      showToast('error', `Failed to load stats: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Load data on mount
  useEffect(() => {
    fetchComponentTypes();
    fetchCategoryStats();
  }, [fetchComponentTypes, fetchCategoryStats]);

  // Seed component types
  const handleSeed = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/component-types/seed', {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        showToast('success', data.message);
        fetchComponentTypes();
        fetchCategoryStats();
      } else {
        throw new Error(data.detail || 'Seed failed');
      }
    } catch (err: any) {
      showToast('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Create or update component type
  const handleSave = async () => {
    try {
      const url = editingType 
        ? `/api/admin/component-types/${editingType.id}`
        : '/api/admin/component-types';
      const method = editingType ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      if (data.success) {
        showToast('success', data.message);
        setDialogOpen(false);
        setEditingType(null);
        resetForm();
        fetchComponentTypes();
      } else {
        throw new Error(data.detail || 'Save failed');
      }
    } catch (err: any) {
      showToast('error', err.message);
    }
  };

  // Delete component type
  const handleDelete = async (type: ComponentType) => {
    if (!window.confirm(`Delete component type "${type.name}"?`)) return;
    
    try {
      const response = await fetch(`/api/admin/component-types/${type.id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.success) {
        showToast('success', data.message);
        fetchComponentTypes();
        fetchCategoryStats();
      } else {
        throw new Error(data.detail || 'Delete failed');
      }
    } catch (err: any) {
      showToast('error', err.message);
    }
  };

  // Open edit dialog
  const handleEdit = (type: ComponentType) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      display_name: type.display_name || '',
      description: type.description || '',
      iec_prefix: type.iec_prefix || '',
      category: type.category || '',
      is_active: type.is_active,
      sort_order: type.sort_order,
    });
    setDialogOpen(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      display_name: '',
      description: '',
      iec_prefix: '',
      category: '',
      is_active: true,
      sort_order: 0,
    });
  };

  // Handle context menu
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, stat: CategoryStats) => {
    setMenuAnchor(event.currentTarget);
    setSelectedStat(stat);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedStat(null);
  };

  // Open move dialog
  const handleMoveOpen = () => {
    setMoveDialogOpen(true);
    handleMenuClose();
  };

  // Categories for dropdown
  const categories = [
    'protection', 'control', 'connection', 'power', 'sensor', 
    'actuator', 'passive', 'indicator', 'safety', 'interface',
    'semiconductor', 'assembly', 'mechanical', 'cooling', 'thermal',
    'distribution', 'other'
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminIcon color="primary" />
            Admin Panel
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage component types and system settings
          </Typography>
        </Box>
        {componentTypes.length === 0 && (
          <Button
            variant="contained"
            startIcon={<SeedIcon />}
            onClick={handleSeed}
            disabled={loading}
          >
            Initialize Default Types
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Component Types" icon={<CategoryIcon />} iconPosition="start" />
          <Tab label="Category Statistics" />
        </Tabs>
      </Paper>

      {/* Component Types Tab */}
      {activeTab === 0 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Master Component Types ({componentTypes.length})
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { resetForm(); setEditingType(null); setDialogOpen(true); }}
            >
              Add Type
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Display Name</TableCell>
                    <TableCell>IEC Prefix</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {componentTypes.map((type) => (
                    <TableRow key={type.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {type.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{type.display_name}</TableCell>
                      <TableCell>
                        {type.iec_prefix && (
                          <Chip label={type.iec_prefix} size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {type.category && (
                          <Chip label={type.category} size="small" color="primary" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={type.is_active ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={type.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(type)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(type)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Category Statistics Tab */}
      {activeTab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Category Usage Statistics
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Component Type</TableCell>
                    <TableCell>Display Name</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>In Master List</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categoryStats.map((stat) => (
                    <TableRow key={stat.component_type} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {stat.component_type || '(empty)'}
                        </Typography>
                      </TableCell>
                      <TableCell>{stat.display_name}</TableCell>
                      <TableCell align="right">
                        <Chip label={stat.count} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        <Chip label={stat.category} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={stat.in_master_list ? 'Yes' : 'No'} 
                          size="small" 
                          color={stat.in_master_list ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleMenuClick(e, stat)}
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMoveOpen}>
          <MoveIcon fontSize="small" sx={{ mr: 1 }} />
          Move all items to different category
        </MenuItem>
        {selectedStat && !selectedStat.in_master_list && (
          <MenuItem onClick={async () => {
            // Add to master list
            try {
              const response = await fetch('/api/admin/component-types', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  name: selectedStat.component_type,
                  display_name: selectedStat.display_name,
                  category: selectedStat.category,
                }),
              });
              const data = await response.json();
              if (data.success) {
                showToast('success', `Added "${selectedStat.component_type}" to master list`);
                fetchComponentTypes();
                fetchCategoryStats();
              }
            } catch (err: any) {
              showToast('error', err.message);
            }
            handleMenuClose();
          }}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />
            Add to master list
          </MenuItem>
        )}
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingType ? 'Edit Component Type' : 'Add Component Type'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
              required
              helperText="Uppercase identifier (e.g., MCB, RELAY)"
            />
            <TextField
              label="Display Name"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              helperText="Human-readable name"
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
            />
            <TextField
              label="IEC 81346 Prefix"
              value={formData.iec_prefix}
              onChange={(e) => setFormData({ ...formData, iec_prefix: e.target.value.toUpperCase() })}
              helperText="Single letter (e.g., Q, K, F)"
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Sort Order"
              type="number"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            {editingType ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move Category Dialog */}
      <Dialog open={moveDialogOpen} onClose={() => setMoveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Move Items to Different Category
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Move all items from <strong>{selectedStat?.component_type}</strong> ({selectedStat?.count} items) to:
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Target Category</InputLabel>
              <Select
                value={moveToCategory}
                label="Target Category"
                onChange={(e) => setMoveToCategory(e.target.value)}
              >
                {componentTypes.filter(t => t.is_active && t.name !== selectedStat?.component_type).map((type) => (
                  <MenuItem key={type.id} value={type.name}>
                    {type.display_name} ({type.name})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            disabled={!moveToCategory}
            onClick={async () => {
              // This would need a bulk move endpoint - for now just show info
              showToast('info', `Would move ${selectedStat?.count} items from ${selectedStat?.component_type} to ${moveToCategory}`);
              setMoveDialogOpen(false);
              setMoveToCategory('');
            }}
          >
            Move Items
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminView;
